import React, { useRef, useEffect, useState } from 'react';
import '../../SimchaWidget.css';

const Cards = ({ initialData = { simchas: [] }, title = 'שמחות בקהילה', showTitle = true }) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const originalContentRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const simchas = (initialData.simchas || []).filter(
    s => !s.expiryDate || new Date(s.expiryDate) > now
  );

  useEffect(() => {
    const checkScroll = () => {
      if (containerRef.current) {
        // חישוב גובה התוכן המקורי בלבד (לא כולל שכפולים)
        const containerHeight = containerRef.current.clientHeight;
        // מחשבים את הגובה של פריט אחד כפול מספר הפריטים
        const itemHeight = simchas.length > 0 ? containerRef.current.querySelector('[data-simcha-item]')?.offsetHeight || 0 : 0;
        const gap = 8; // הרווח בין פריטים
        const contentHeight = simchas.length * (itemHeight + gap);

        // רק אם התוכן גדול משמעותית מהקונטיינר - נפעיל אנימציה
        const threshold = containerHeight * 0.1; // 10% מגובה הקונטיינר
        setNeedsScroll(contentHeight > containerHeight + threshold);
      }
    };

    // מחכים שה-DOM יתעדכן לפני הבדיקה
    const timer = setTimeout(checkScroll, 100);

    const resizeObserver = new ResizeObserver(checkScroll);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [simchas]);

  const boldLabel = (type, label, boldWords) => {
    if (type !== 'other') return <strong>{label}</strong>;
    if (!boldWords?.length) return label;
    return label.split(/(\s+)/).map((w, i) =>
      boldWords.includes(w.trim()) && w.trim() ? <strong key={i}>{w}</strong> : w
    );
  };

  const getSimchaConfig = (type, customType) => {
    const configs = {
      birth_boy:       { icon: 'child_care',  label: 'הולדת הבן',  color: '#BF360C' },
      birth_girl:      { icon: 'child_care',  label: 'הולדת הבת',  color: '#BF360C' },
      grandchild_boy:  { icon: 'child_care',  label: 'הולדת נכד',  color: '#BF360C' },
      grandchild_girl: { icon: 'child_care',  label: 'הולדת נכדה', color: '#BF360C' },
      engagement_boy:  { icon: 'favorite',    label: 'אירוסין',    color: '#BF360C' },
      engagement_girl: { icon: 'favorite',    label: 'אירוסין',    color: '#BF360C' },
      wedding_boy:     { icon: 'celebration', label: 'חתונה',      color: '#BF360C' },
      wedding_girl:    { icon: 'celebration', label: 'חתונה',      color: '#BF360C' },
      bar_mitzvah:     { icon: 'menu_book',   label: 'בר מצווה',   color: '#BF360C' },
      other:           { icon: 'celebration', label: customType || 'שמחה', color: '#BF360C' },
    };
    return configs[type] || { icon: 'celebration', label: customType || 'שמחה בקהילה', color: '#BF360C' };
  };

  return (
    <div style={{
      padding: '12px',
      direction: 'rtl',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      fontSize: 'inherit' // יורש מה-WidgetWrapper
    }}>
      {/* Header - כותרת השמחות */}
      {showTitle && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          borderBottom: '0.0625em solid #FFE0B2',
          paddingBottom: '8px',
          marginBottom: '10px'
        }}>
          <span className="material-symbols-outlined" style={{ color: '#E65100', fontSize: '0.875em' }}>celebration</span>
          <h3 style={{
            margin: 0,
            fontWeight: 'bold',
            color: '#BF360C',
            fontSize: '0.875em',
          }}>{title}</h3>
        </div>
      )}

      {/* רשימת כרטיסי השמחה */}
      <div ref={containerRef} style={{ overflow: 'hidden', flex: 1, position: 'relative', width: '100%' }}>
        {simchas.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#795548', fontSize: '0.875em', padding: '10px' }}>אין שמחות כרגע</div>
        ) : (
          <>
            {needsScroll && (
              <style>
                {`
                  @keyframes scrollSimchas {
                    0% { transform: translateY(0); }
                    100% { transform: translateY(calc(-100% / 3)); }
                  }
                `}
              </style>
            )}
            <div ref={contentRef} style={{
              animation: needsScroll ? 'scrollSimchas 30s linear infinite' : 'none'
            }}>
              <div ref={originalContentRef} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                width: '100%'
              }}>
                {simchas.map((simcha, index) => {
                  const config = getSimchaConfig(simcha.type, simcha.customType);
                  return (
                    <div
                      key={simcha.id || index}
                      data-simcha-item
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5em',
                        backgroundColor: '#FFF3E0',
                        padding: '0.625em',
                        borderRadius: '1.25em',
                        border: '0.03125em solid #FFCCBC',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <div style={{
                        backgroundColor: '#FFF3E0',
                        padding: '6px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                        color: '#BF360C'
                      }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1.125em' }}>
                          {config.icon}
                        </span>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.875em', fontWeight: 'bold', color: '#3E2723' }}>
                          {simcha.name}
                        </div>
                        <div style={{ fontSize: '0.6875em', color: '#795548' }}>
                          ל{boldLabel(simcha.type, config.label, simcha.customTypeBold)}{simcha.celebrant ? ` של ${simcha.celebrant}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {needsScroll && (
                  <>
                    {simchas.map((simcha, index) => {
                      const config = getSimchaConfig(simcha.type, simcha.customType);
                      return (
                        <div
                          key={`dup1-${simcha.id || index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5em',
                            backgroundColor: '#FFF3E0',
                            padding: '0.625em',
                            borderRadius: '1.25em',
                            border: '0.03125em solid #FFCCBC',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div style={{
                            backgroundColor: '#FFF3E0',
                            padding: '6px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                            color: '#BF360C'
                          }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '1.125em' }}>
                              {config.icon}
                            </span>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875em', fontWeight: 'bold', color: '#3E2723' }}>
                              {simcha.name}
                            </div>
                            <div style={{ fontSize: '0.6875em', color: '#795548' }}>
                              ל{boldLabel(simcha.type, config.label, simcha.customTypeBold)}{simcha.celebrant ? ` של ${simcha.celebrant}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {simchas.map((simcha, index) => {
                      const config = getSimchaConfig(simcha.type, simcha.customType);
                      return (
                        <div
                          key={`dup2-${simcha.id || index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5em',
                            backgroundColor: '#FFF3E0',
                            padding: '0.625em',
                            borderRadius: '1.25em',
                            border: '0.03125em solid #FFCCBC',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div style={{
                            backgroundColor: '#FFF3E0',
                            padding: '6px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                            color: '#BF360C'
                          }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '1.125em' }}>
                              {config.icon}
                            </span>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.875em', fontWeight: 'bold', color: '#3E2723' }}>
                              {simcha.name}
                            </div>
                            <div style={{ fontSize: '0.6875em', color: '#795548' }}>
                              ל{boldLabel(simcha.type, config.label, simcha.customTypeBold)}{simcha.celebrant ? ` של ${simcha.celebrant}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cards;


